import React, { useState, useEffect } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function NoticeBoardSetup() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
  } = useForm({
    defaultValues: {
      id: 0,
      title: "",
      content: "",
      centerClassId: null,
      classSectionId: null,
      isPublished: false,
      publishDate: "",
      fileName: "",
      filePath: null,
    },
  });

  const selectedClass = useWatch({ control, name: "centerClassId" });
  const isPublished = useWatch({ control, name: "isPublished" });
  const [classesData, setClassesData] = useState([]);
  const [noticesData, setNoticesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileName, setFileName] = useState("");

  const [triggerRerender, setTriggerRerender] = useState(false);

  const [highlightedRow, setHighlightedRow] = useState(null);
  const [highlightLatest, setHighlightLatest] = useState(false);

  const getNotices = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Dashboard/GetAllNotices?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNoticesData(response);
    } catch (error) {
      console.error("Error fetching notices:", error);
    }
  };

  useEffect(() => {
    getNotices();
  }, [triggerRerender]);

  // Fetch classes
  const fetchClasses = async () => {
    try {
      const response = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClassesData(response || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to fetch classes.");
    }
  };

  useEffect(() => {
    if (token && centerId) fetchClasses();
  }, [token, centerId]);

  // Fetch sections based on class
  useEffect(() => {
    if (selectedClass && selectedClass.value) {
      const fetchSections = async () => {
        try {
          const response = await get(
            `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${selectedClass.value}&centerId=${centerId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setClassSectionsData(response || []);
        } catch (error) {
          console.error("Error fetching sections:", error);
          toast.error("Failed to fetch sections.");
        }
      };
      fetchSections();
    } else {
      setClassSectionsData([]);
      setValue("classSectionId", null);
    }
  }, [selectedClass, token, centerId, setValue]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const maxSizeInMB = 2;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error(
          `File size exceeds ${maxSizeInMB}MB limit. Please choose a smaller file.`
        );
        event.target.value = null;
        return;
      }

      setFileName(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue("base64String", base64String, { shouldValidate: true });
        setPreviewUrl(reader.result);
        trigger("fileName");
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
      setFileName(null);
      setValue("base64String", "");
      setValue("fileName", "");
      setValue("filePath", "");
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 80,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "title",
        header: "Title",
        // size: 250,
      },
      {
        accessorKey: "isPublished",
        header: "Published",
        // size: 120,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <i className="fas fa-circle-check text-success"></i>
          ) : (
            <i className="fas fa-times-circle text-danger"></i>
          ),
      },
      {
        accessorKey: "publishDate",
        header: "Publish Date",
        // size: 180,
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString()
            : "—",
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn p-0 text-primary"
              onClick={() => handleEdit(row.original)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0 text-primary"
              onClick={() => handleDelete(row.original.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = noticesData.map((row, index) => ({
      "Sr No": index + 1,
      Title: row.title,
      Published: row.isPublished ? "Yes" : "No",
      "Publish Date": row.publishDate
        ? new Date(row.publishDate).toLocaleDateString()
        : "—",
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notices");
    XLSX.writeFile(workbook, "Notices.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleId == "2" ? orgLogo : roleId == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Notice Board";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) =>
        column.accessorKey !== "actions" &&
        column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = noticesData.map((row, index) => {
      return [
        index + 1,
        row.title,
        row.isPublished ? "Yes" : "No",
        row.publishDate ? new Date(row.publishDate).toLocaleDateString() : "—",
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("NoticeBoard.pdf");
  };

  const handleEdit = async (row) => {
    // Fetch classes if not loaded and needed
    if (!classesData.length && row.centerClassId) {
      try {
        await fetchClasses();
      } catch (error) {
        toast.error("Failed to fetch classes.");
        return;
      }
    }

    const selectedClass = classesData.find(
      (item) => item.id === row.centerClassId
    );

    let selectedClassSection = null;
    if (row.centerClassId && row.classSectionId) {
      try {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${row.centerClassId}&centerId=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassSectionsData(response || []);
        selectedClassSection = response.find(
          (item) => item.id === row.classSectionId
        );
      } catch (error) {
        console.error("Error fetching sections:", error);
        toast.error("Failed to fetch sections.");
        return;
      }
    } else {
      setClassSectionsData([]);
    }

    reset({
      id: row.id || 0,
      title: row.title || "",
      content: row.content || "",
      centerClassId: selectedClass
        ? {
            value: selectedClass.id,
            label: selectedClass.name || "",
          }
        : null,
      classSectionId: selectedClassSection
        ? {
            value: selectedClassSection.id,
            label: selectedClassSection.name || "",
          }
        : null,
      isPublished: row.isPublished === 1,
      publishDate: row.publishDate ? row.publishDate.split("T")[0] : "",
      fileName: row.fileName || "",
      filePath: row.filePath || null,
    });

    if (row.filePath) {
      const imageUrl = `${
        process.env.REACT_APP_LIVE_DOMAIN_URL
      }ExternalImages/${row.filePath.replace(/\\/g, "/")}`;
      setPreviewUrl(imageUrl);
      setFileName(row.fileName);
    } else {
      setPreviewUrl(null);
      setFileName("");
    }

    setHighlightedRow(row.id);
    setHighlightLatest(false);
  };

  const handleDelete = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this notice?"
    );

    if (confirmDelete) {
      try {
        const response = await get(`Dashboard/DeleteNotice?noticeId=${rowId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.isSuccess) {
          toast.success(response.message || "Notice deleted successfully!");
          setTriggerRerender((prev) => !prev);
          setHighlightedRow(null);
          setHighlightLatest(false);
          reset();
        } else {
          toast.error(response.message || "Failed to delete notice.");
        }
      } catch (error) {
        console.error("Error deleting notice:", error);
        toast.error("Error deleting notice.");
      }
    }
  };

  const saveData = async (formData) => {
    const payload = {
      id: formData.id || 0,
      title: formData.title,
      centerId: parseInt(centerId),
      content: formData.content,
      centerClassId: formData.centerClassId?.value || null,
      classSectionId: formData.classSectionId?.value || null,
      createdBy: parseInt(userId),
      isPublished: formData.isPublished ? 1 : 0,
      publishDate: formData.isPublished
        ? new Date(formData.publishDate).toISOString()
        : null,
      publishedBy: formData.isPublished ? parseInt(userId) : 0,
      fileName: fileName || null,
      filePath: formData.filePath || null,
      base64String: formData.base64String || null,
      updatedBy: parseInt(userId),
    };

    try {
      const response = await post("Dashboard/UpdateNotice", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.isSuccess) {
        toast.success("Notice saved successfully!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          title: "",
          content: "",
          centerClassId: null,
          classSectionId: null,
          isPublished: false,
          publishDate: "",
          fileName: "",
          filePath: null,
        });
        setPreviewUrl(null);
        setFileName("");

        if (payload.id) {
          setHighlightedRow(payload.id);
          setTimeout(() => setHighlightedRow(null), 3000);
        } else {
          setHighlightLatest(true);
        }
      } else {
        toast.error(response.message || "An error occurred.");
      }
    } catch (error) {
      console.error("Error saving notice:", error);
      toast.error("Failed to save notice.");
    }
  };

  useEffect(() => {
    if (highlightLatest && noticesData.length > 0) {
      const firstRow = noticesData[0];
      setHighlightedRow(firstRow.id);
      setTimeout(() => {
        setHighlightedRow(null);
        setHighlightLatest(false);
      }, 3000);
    }
  }, [noticesData, highlightLatest]);

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Notice Board Setup</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <form
                onSubmit={handleSubmit(saveData)}
                className="row g-3 new-form-style"
              >
                {/* Title */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Title<span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("title", { required: "Title is required." })}
                    type="text"
                    className="form-control"
                  />
                  {errors.title && (
                    <small className="text-danger">
                      {errors.title.message}
                    </small>
                  )}
                </div>

                {/* Content */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Content<span className="text-danger">*</span>
                  </label>
                  <textarea
                    {...register("content", {
                      required: "Content is required.",
                    })}
                    className="form-control"
                    rows={1}
                  ></textarea>
                  {errors.content && (
                    <small className="text-danger">
                      {errors.content.message}
                    </small>
                  )}
                </div>

                {/* Year Group */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Year Group
                  </label>
                  <Controller
                    name="centerClassId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classesData.map((cls) => ({
                          value: cls.id,
                          label: cls.name,
                        }))}
                        onChange={(selected) => {
                          field.onChange(selected);
                          setValue("classSectionId", null);
                        }}
                        isClearable
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                </div>

                {/* Section */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Year Group Form
                  </label>
                  <Controller
                    name="classSectionId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classSectionsData.map((sec) => ({
                          value: sec.id,
                          label: sec.name,
                        }))}
                        isClearable
                        isDisabled={!selectedClass}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                </div>

                {/* File Upload */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Attachment (PNG/JPG)
                  </label>
                  <div className="image-uploader-wrapper-2 d-flex flex-column align-items-center">
                    <div className="image-preview d-flex justify-content-center align-items-center flex-shrink-0 mb-2 mt-1">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Preview" />
                      ) : (
                        <div>
                          <span className="fas fa-image fs-3 text-light"></span>
                        </div>
                      )}
                    </div>
                    <input
                      {...register("fileName")}
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleFileChange}
                    />
                  </div>
                  <span className="text-body-secondary fs-10">
                    (Max file size: 2MB, Recommended resolution: 350 x 200 px)
                  </span>
                  {errors.fileName && (
                    <small className="text-danger">
                      {errors.fileName.message}
                    </small>
                  )}
                </div>

                {/* Publish */}
                <div className="col-md-6">
                  <div className="col-12 ">
                    <div className="form-check">
                      <input
                        {...register("isPublished")}
                        type="checkbox"
                        className="form-check-input"
                        id="isPublished"
                      />
                      <label className="form-check-label" htmlFor="isPublished">
                        Publish
                      </label>
                    </div>
                  </div>

                  {isPublished && (
                    <div className="col-12 mt-3">
                      <label className="form-label form-label-new">
                        Publish Date<span className="text-danger">*</span>
                      </label>
                      <input
                        {...register("publishDate", {
                          required: isPublished
                            ? "Publish date is required."
                            : false,
                        })}
                        type="date"
                        className="form-control"
                        min={new Date().toISOString().split("T")[0]} // Set min to today's date
                      />
                      {errors.publishDate && (
                        <small className="text-danger">
                          {errors.publishDate.message}
                        </small>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right Side - Listing */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={noticesData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NoticeBoardSetup;
